import { useMemo } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Select, TextField } from "@shapr3d/frontend-lib";
import { Controller, type FieldError, useForm } from "react-hook-form";
import { z } from "zod";

import useAccount from "../../hooks/useAccount";
import useTranslation from "../../hooks/useTranslation";
import FilePicker from "../FilePicker/FilePicker";
import Heading from "../Heading/Heading";

import style from "./EduForm.module.css";

enum Role {
  Student = "STUDENT",
  Teacher = "TEACHER",
  FacultyMember = "FACULTY_MEMBER",
}
interface EduFormProps {
  submit: (data: EduFormInputs) => void;
  showStudentIDUpload: boolean;

  isSubmitting?: boolean;
  errorMessage?: string;
}
export interface EduFormInputs {
  role: Role;
  firstName: string;
  lastName: string;
  school: string;
  major: string;
  idPhotos: File[] | undefined;
}

const getValidationSchema = (
  showStudentIDUpload: boolean,
  requiredFieldMsg: string,
  uploadImageMsg: string
) => {
  const schema = z.object({
    role: z.nativeEnum(Role, { required_error: requiredFieldMsg }),
    firstName: z.string().nonempty(requiredFieldMsg),
    lastName: z.string().nonempty(requiredFieldMsg),
    school: z.string().nonempty(requiredFieldMsg),
    major: z.string().nonempty(requiredFieldMsg),
  });

  const idPhotoSchema = z.object({
    idPhotos: z.array(z.unknown()).nonempty(uploadImageMsg),
  });

  if (showStudentIDUpload) {
    return schema.merge(idPhotoSchema);
  }
  return schema;
};

const EduForm = (props: EduFormProps) => {
  const { data: accountData } = useAccount();
  const { t, m } = useTranslation();
  const resolver = useMemo(
    () =>
      zodResolver(
        getValidationSchema(
          props.showStudentIDUpload,
          t(m.Edu.RequiredField),
          t(m.Edu.PleaseUploadAtLeastOneImage)
        )
      ),
    [props.showStudentIDUpload, t, m]
  );
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<EduFormInputs>({ resolver });

  return (
    <form onSubmit={handleSubmit(props.submit)} noValidate>
      <div className={style.modalContainer}>
        <Heading type="h1" className={style.heading}>
          {t(m.Edu.FormHeading)}
        </Heading>
        <p>{t(m.Edu.FormDescription)}</p>
        <Controller
          control={control}
          name="role"
          render={({ field: { onChange } }) => (
            <Select
              errorMessage={errors.role?.message}
              error={Boolean(errors.role)}
              disabled={props.isSubmitting}
              onChange={(event) => onChange(event.target.value)}
              required
              defaultValue={""}
            >
              <option value="" disabled>
                {t(m.Edu.RolePlaceholder)}
              </option>
              <option value={Role.Teacher}>{t(m.Edu.RoleTeacher)}</option>
              <option value={Role.Student}>{t(m.Edu.RoleStudent)}</option>
              <option value={Role.FacultyMember}>{t(m.Edu.RoleFacultyMember)}</option>
            </Select>
          )}
        />
        <TextField
          errorMessage={errors.firstName?.message}
          placeholder={t(m.Edu.FirstName)}
          disabled={props.isSubmitting}
          error={Boolean(errors.firstName)}
          {...register("firstName")}
        />

        <TextField
          errorMessage={errors.lastName?.message}
          placeholder={t(m.Edu.LastName)}
          disabled={props.isSubmitting}
          error={Boolean(errors.lastName)}
          {...register("lastName")}
        />

        <TextField
          errorMessage={errors.school?.message}
          placeholder={t(m.Edu.SchoolName)}
          disabled={props.isSubmitting}
          error={Boolean(errors.school)}
          {...register("school")}
        />

        <TextField
          errorMessage={errors.major?.message}
          placeholder={t(m.Edu.MajorAreaOfStudy)}
          disabled={props.isSubmitting}
          error={Boolean(errors.major)}
          {...register("major")}
        />

        {props.showStudentIDUpload && (
          <>
            <Controller
              control={control}
              name="idPhotos"
              render={({ field: { onChange } }) => (
                <FilePicker
                  disabled={props.isSubmitting}
                  title={
                    accountData?.countryCode === "CN"
                      ? t(m.Edu.PhotosOfSchoolIdCN, {
                          link: (chunks) => <a href="https://my.chsi.com.cn">{chunks}</a>,
                        })
                      : t(m.Edu.PhotosOfSchoolId)
                  }
                  maxFiles={2}
                  maxFileSizeInMB={1}
                  acceptedFileExtensions={["JPG", "JPEG", "PNG", "GIF", "BMP", "SVG", "WebP"]}
                  onChange={onChange}
                />
              )}
            />
            {(errors.idPhotos as FieldError | undefined)?.message && (
              <span className={style.error}>
                {(errors.idPhotos as FieldError | undefined)?.message}
              </span>
            )}
          </>
        )}

        <Button type="submit" disabled={props.isSubmitting}>
          {t(m.Edu.SendRequest)}
        </Button>
        {props.errorMessage && <span className={style.error}>{props.errorMessage}</span>}

        <div className={style.disclaimer}>{t(m.Edu.DisclaimerMember)}</div>
        <div className={style.disclaimer}>
          {t(m.Edu.DisclaimerProTrial, {
            strong: (chunks) => <strong>{chunks}</strong>,
          })}
        </div>
      </div>
    </form>
  );
};

export default EduForm;
